import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Play, Pause } from 'lucide-react';

export function formatVoiceDuration(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function useVoiceWaveformBars(src, count = 28) {
  return useMemo(() => {
    const out = [];
    let seed = 0;
    for (let i = 0; i < (src || '').length; i++) seed = (seed * 31 + src.charCodeAt(i)) >>> 0;
    if (!seed) seed = 1;
    for (let i = 0; i < count; i++) {
      seed = (seed * 1103515245 + 12345) >>> 0;
      const t = (seed % 1000) / 1000;
      out.push(0.28 + t * 0.72);
    }
    return out;
  }, [src, count]);
}

export default function VoiceMessagePlayer({ src, isOutgoing, isDarkMode }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const bars = useVoiceWaveformBars(src);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onLoaded = () => setDuration(Number.isFinite(a.duration) ? a.duration : 0);
    const onTime = () => setCurrentTime(a.currentTime);
    const onEnded = () => {
      setPlaying(false);
      setCurrentTime(0);
      if (a) a.currentTime = 0;
    };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    a.addEventListener('loadedmetadata', onLoaded);
    a.addEventListener('durationchange', onLoaded);
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('ended', onEnded);
    a.addEventListener('play', onPlay);
    a.addEventListener('pause', onPause);
    return () => {
      a.removeEventListener('loadedmetadata', onLoaded);
      a.removeEventListener('durationchange', onLoaded);
      a.removeEventListener('timeupdate', onTime);
      a.removeEventListener('ended', onEnded);
      a.removeEventListener('play', onPlay);
      a.removeEventListener('pause', onPause);
    };
  }, [src]);

  const progress = duration > 0 ? Math.min(1, currentTime / duration) : 0;
  const displayTime = playing || currentTime > 0 ? currentTime : duration;

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) a.pause();
    else a.play().catch(() => {});
  };

  const seekFromEvent = (e) => {
    const a = audioRef.current;
    if (!a || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, x / rect.width));
    a.currentTime = ratio * duration;
  };

  const playBtnClass = isOutgoing
    ? 'bg-white text-forest-ondark ring-1 ring-black/5'
    : 'bg-forest-ondark text-white';

  const barBase = isOutgoing
    ? 'bg-white/35'
    : isDarkMode
      ? 'bg-canvas'
      : 'bg-line';

  const barPlayed = isOutgoing ? 'bg-white' : 'bg-forest-ondark';

  const timeClass = isOutgoing
    ? 'text-white/90'
    : isDarkMode
      ? 'text-ink-muted-dark'
      : 'text-ink-muted';

  return (
    <div className="flex items-center gap-2.5 min-w-[240px] max-w-[min(100%,280px)] select-none" dir="ltr">
      <audio ref={audioRef} src={src} preload="metadata" className="hidden" />
      <button
        type="button"
        onClick={togglePlay}
        aria-label={playing ? 'Pause voice message' : 'Play voice message'}
        className={`flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center transition-transform duration-[120ms] ease-out active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-ondark focus-visible:ring-offset-2 ${playBtnClass}`}
      >
        {playing ? (
          <Pause size={18} fill="currentColor" />
        ) : (
          <Play size={18} fill="currentColor" className="ml-0.5" />
        )}
      </button>
      <div className="flex-1 flex flex-col gap-1 min-w-0 justify-center">
        <button
          type="button"
          onClick={seekFromEvent}
          className="flex items-end gap-px h-8 w-full cursor-pointer rounded-md px-0.5 outline-none focus-visible:ring-2 focus-visible:ring-forest-ondark/60"
          aria-label="Seek in voice message"
        >
          {bars.map((h, i) => {
            const t = (i + 0.5) / bars.length;
            const played = t <= progress;
            return (
              <span
                key={i}
                className={`flex-1 min-w-[2px] max-w-[4px] rounded-full ${played ? barPlayed : barBase}`}
                style={{ height: `${Math.round(h * 100)}%` }}
              />
            );
          })}
        </button>
        <div className={`text-[11px] tabular-nums font-medium leading-none ${timeClass}`}>
          {formatVoiceDuration(displayTime)}
        </div>
      </div>
    </div>
  );
}
